/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import type * as React from "react";
import type * as Urql from "urql";

// Default check for query data
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function defaultIsEmptyCheck(data: any) {
  if ((Array.isArray(data) && data.length === 0) || data === null) {
    return true;
  }
  return false;
}

/** https://app.gitbook.com/o/-LlhZY09V4VTR7cy_55t/s/-MSitZZVNivUi5eDfPNe/guidelines/frontend/normalize-request-handling/~/c/15e6745a1d184fb9b922c05bd731cc4c */
const withQuery =
  <Data, Variables, AfterQueryData = Data>({
    afterQuery,
    forceLoading,
    response,
  }: {
    response: Urql.UseQueryState<Data, Variables>;
    afterQuery?: (data: Data) => AfterQueryData;
    /* Force loading status for dev or specific behavior */
    forceLoading?: boolean;
  }) =>
  ({
    Empty,
    Error,
    Loading,
    Success,
  }: {
    Empty?:
      | React.ReactElement
      | {
          isEmpty: (
            data: typeof afterQuery extends undefined ? Data : AfterQueryData
          ) => boolean;
          component: React.ReactElement;
        };
    Error?: React.ReactElement;
    Loading?: React.ReactElement;
    Success: (
      data: NonNullable<
        typeof afterQuery extends undefined ? Data : AfterQueryData
      >
    ) => React.ReactElement;
  }) => {
    if ((Loading && response.fetching) || (Loading && forceLoading)) {
      return Loading;
    }

    if (Error && response.error) {
      return Error;
    }

    if (response.data) {
      const data = afterQuery?.(response.data) ?? response.data;

      if (Empty) {
        const isEmpty =
          // @ts-expect-error safe to ignore
          "isEmpty" in Empty ? Empty.isEmpty(data) : defaultIsEmptyCheck(data);

        if (isEmpty) {
          return "component" in Empty ? Empty.component : Empty;
        }
      }

      // @ts-expect-error safe to ignore
      return Success(data);
    }

    return null;
  };

export { withQuery };
